/* eslint-disable react/no-unused-state */

import type { ReactElement, ReactNode } from 'react';
import { Component } from 'react';

import get from 'lodash/get';
import isArray from 'lodash/isArray';

import PoiDataContext from './contexts/PoiDataContext';

import type { PoiDataState } from './types';
import type { BackendGateway } from '@shared/types/frontend/backend-gateway';
import type { Logger } from '@shared/types/frontend/logger';
import type {
  PoiData,
  PoiDataArray,
} from '@shared/types/frontend/nearby-transport-response';
import type { HotelContent } from '@shared/types/hotel-content';
import type { EntityId, Nullable } from '@shared/types/utils';

import { MAX_POI_ITEM_SINGLE } from '@hotels-hotel-detail/utils/location-constants';
import { AIRPORT } from '@shared/constants/poi-types';
import { withBackendContext } from '@shared/data-access-frontend/contexts/backend-context';
import { withLogging } from '@shared/utils-frontend/contexts/LoggingContext';
import { buildHotelLocation } from '@shared/utils-universal/hotel-location';

type Props = {
  logger: Logger;
  backendGateway: BackendGateway;
  children: ReactElement | ReactNode[];
  uBFFHotel: HotelContent;
  searchEntityId: Nullable<string>;
};

const resort = (nearbyTransport: PoiDataArray) => {
  const temp: { [key: string]: string } = {};
  const tempArr: PoiDataArray = [];
  const result = nearbyTransport.slice();
  nearbyTransport.slice(0, MAX_POI_ITEM_SINGLE).forEach((item, index) => {
    if (temp[item.poiName]) {
      result.splice(index - tempArr.length, 1);
      tempArr.push(item);
    } else {
      temp[item.poiName] = item.poiName;
    }
  });
  result.splice(MAX_POI_ITEM_SINGLE, 0, ...tempArr);
  return result;
};
class PoiDataProvider extends Component<Props, PoiDataState> {
  constructor(props: Props) {
    super(props);

    // @ts-expect-error 'selectedTab' missing defined
    this.state = {
      onPoiMarkerClick: this.onPoiMarkerClick,
      onTabClick: this.onTabClick,
      selectedEntityId: null,
    };
  }

  async componentDidMount() {
    const { uBFFHotel } = this.props;
    const lat = get(uBFFHotel, 'location.coordinates.latitude', null);
    const lng = get(uBFFHotel, 'location.coordinates.longitude', null);
    if (!lat || !lng) {
      return;
    }

    const [nearbyPoi, transport] = await Promise.all([
      this.fetchPoi({
        lat,
        lon: lng,
        minDistance: 3000,
        maxDistance: 5000,
        poiNumber: 10,
      }),
      this.fetchTransport({
        lat,
        lon: lng,
        limit: 2,
      }),
    ]);

    this.setState({ ...nearbyPoi, ...transport });
  }

  fetchPoi = async (poiParams: {
    lat: number;
    lon: number;
    minDistance: number;
    maxDistance: number;
    poiNumber: number;
  }) => {
    const {
      backendGateway,
      logger,
      searchEntityId,
      uBFFHotel: { distanceReference },
    } = this.props;

    const { entity } = distanceReference ?? {};

    const entityId =
      entity && entity.type !== 'City' ? entity.id : searchEntityId;

    try {
      const nearbyPoi = await backendGateway.nearbyPoiResultV2({
        ...poiParams,
        entityId,
      });
      if (!isArray(nearbyPoi) || nearbyPoi.length === 0) {
        return {};
      }

      return { nearbyPoi };
    } catch (e: Error | unknown) {
      logger.error('Get nearbyPoiResultV2 error', poiParams, e);
    }

    return {};
  };

  fetchTransport = async (transportParams: {
    lat: number;
    lon: number;
    limit: number;
  }) => {
    const { backendGateway, logger, uBFFHotel } = this.props;
    const { cityId } = buildHotelLocation(uBFFHotel.location);
    if (!cityId) {
      return {};
    }

    try {
      const nearbyTransport = await backendGateway.nearbyTransport({
        ...transportParams,
        cityId,
      });
      if (!isArray(nearbyTransport)) {
        return {};
      }

      const airport = nearbyTransport.filter(
        (item: PoiData) => item.poiType === AIRPORT,
      );
      const transportWithoutAirport = nearbyTransport.filter(
        (item: PoiData) => item.poiType !== AIRPORT,
      );

      return {
        airport,
        transportWithoutAirport: resort(transportWithoutAirport),
      };
    } catch (e: Error | unknown) {
      logger.error('Get nearbyTransport error', transportParams, e);
    }

    return {};
  };

  onPoiMarkerClick = (selectedEntityId: Nullable<EntityId>) => {
    this.setState({
      selectedEntityId,
    });
  };

  onTabClick = (name: string) => {
    this.setState({
      selectedTab: name,
    });
  };

  render() {
    return (
      <PoiDataContext.Provider value={this.state}>
        {this.props.children}
      </PoiDataContext.Provider>
    );
  }
}

export default withBackendContext(withLogging(PoiDataProvider));
